import { useState, useMemo } from 'react';
import { X, Wand2, Check, AlertCircle, Loader2 } from 'lucide-react';

/**
 * Dialog per recuperare categorie "fantasma" (presenti nelle transazioni/conti ma non nella lista categorie).
 *
 * Props:
 *  - missingCategories: { expense: string[], income: string[], wallet: string[] }
 *  - onConfirm(toCreate: { type, name, icon }[]) => void
 *  - onClose() => void
 *  - loading: boolean
 */
// Helper per pre-assegnare l'emoji corretta in base al nome della categoria/conto
function getBestEmoji(name) {
  const normalized = name.toLowerCase().trim();

  if (normalized.includes('sushi')) return '🍣';
  if (normalized.includes('gelato')) return '🍦';
  if (normalized.includes('bar')) return '☕';
  if (normalized.includes('regal')) return '🎁';
  if (normalized.includes('benessere') || normalized.includes('spa') || normalized.includes('salute') || normalized.includes('cura')) return '💆';
  if (normalized.includes('elettronic') || normalized.includes('tecnolog')) return '💻';
  if (normalized.includes('pallavolo') || normalized.includes('volley')) return '🏐';
  if (normalized.includes('sport') || normalized.includes('allenamento') || normalized.includes('palestra')) return '⚽';
  if (normalized.includes('investiment') || normalized.includes('crypto') || normalized.includes('azion') || normalized.includes('titoli')) return '📈';
  if (normalized.includes('animazion') || normalized.includes('cinema') || normalized.includes('film') || normalized.includes('spettacol')) return '🎬';
  if (normalized.includes('ripetizion') || normalized.includes('lezion') || normalized.includes('studio')) return '📚';
  if (normalized.includes('istruzion') || normalized.includes('scuola') || normalized.includes('universit') || normalized.includes('cors')) return '🎓';
  if (normalized.includes('deposito') || normalized.includes('banca') || normalized.includes('risparmi')) return '🏦';

  // Altri abbinamenti comuni di fallback
  if (normalized.includes('alimentar') || normalized.includes('cibo') || normalized.includes('spesa') || normalized.includes('supermercat')) return '🍽️';
  if (normalized.includes('casa') || normalized.includes('affitt') || normalized.includes('arredament')) return '🏠';
  if (normalized.includes('aut') || normalized.includes('macchin') || normalized.includes('viagg') || normalized.includes('vettur')) return '🚗';
  if (normalized.includes('bollett') || normalized.includes('luce') || normalized.includes('gas') || normalized.includes('acqua')) return '💡';
  if (normalized.includes('stipendio') || normalized.includes('paga') || normalized.includes('lavoro')) return '💰';

  return '📦'; // Default
}

function RecoverCategoriesDialog({ missingCategories, onConfirm, onClose, loading }) {
  // Build initial list of items: { type, name, selected, icon }
  const initialItems = useMemo(() => {
    const result = [];
    const types = ['expense', 'income', 'wallet'];
    for (const type of types) {
      (missingCategories[type] || []).forEach(name => {
        result.push({ type, name, selected: true, icon: getBestEmoji(name) });
      });
    }
    return result;
  }, [missingCategories]);

  const [items, setItems] = useState(initialItems);

  const totalMissing = items.length;
  const totalSelected = items.filter(i => i.selected).length;

  const typeLabel = { expense: 'Spesa', income: 'Entrata', wallet: 'Conto' };
  const typeBadgeColor = {
    expense: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    income: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    wallet: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
  };

  const toggleItem = (index) => {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, selected: !item.selected } : item));
  };

  const setIcon = (index, emoji) => {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, icon: emoji } : item));
  };

  const selectAll = () => setItems(prev => prev.map(item => ({ ...item, selected: true })));
  const deselectAll = () => setItems(prev => prev.map(item => ({ ...item, selected: false })));

  const handleConfirm = () => {
    const toCreate = items.filter(i => i.selected).map(({ type, name, icon }) => ({
      type,
      name,
      icon: icon.trim() || '📦'
    }));
    if (toCreate.length === 0) return;
    onConfirm(toCreate);
  };

  if (totalMissing === 0) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[99999] flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center animate-fade-in-up">
          <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Tutto in ordine!</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Non ci sono categorie mancanti. Tutte le categorie usate nelle transazioni e nei conti sono già presenti.
          </p>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-colors"
          >
            Chiudi
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[99999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col animate-fade-in-up"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-5 rounded-t-2xl flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <Wand2 className="w-5 h-5" />
            <div>
              <h2 className="font-bold text-base">Recupera Categorie</h2>
              <p className="text-xs text-white/70 mt-0.5">{totalMissing} categorie trovate nelle transazioni</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Info banner */}
        <div className="flex items-start gap-2 mx-5 mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800/40 text-xs text-amber-800 dark:text-amber-300 flex-shrink-0">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <p>
            Queste categorie sono usate nelle tue transazioni/conti ma non esistono più nell'elenco.
            Seleziona quelle da ricreare e <strong>inserisci l'emoji preferita digitandola liberamente dalla tua tastiera</strong>.
          </p>
        </div>

        {/* Select all / Deselect all */}
        <div className="flex items-center justify-between mx-5 mt-3 flex-shrink-0">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {totalSelected} di {totalMissing} selezionate
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={selectAll}
              className="text-xs text-purple-600 dark:text-purple-400 hover:underline"
            >
              Seleziona tutte
            </button>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <button
              type="button"
              onClick={deselectAll}
              className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
            >
              Deseleziona tutte
            </button>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto mx-5 mt-3 space-y-2 pb-2">
          {items.map((item, index) => (
            <div
              key={`${item.type}-${item.name}`}
              className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                item.selected
                  ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-700/50'
                  : 'bg-gray-50 dark:bg-gray-700/30 border-gray-200 dark:border-gray-700 opacity-60'
              }`}
            >
              {/* Checkbox */}
              <button
                type="button"
                onClick={() => toggleItem(index)}
                className={`w-5 h-5 rounded-md flex-shrink-0 border-2 flex items-center justify-center transition-colors ${
                  item.selected
                    ? 'bg-purple-600 border-purple-600'
                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600'
                }`}
              >
                {item.selected && <Check className="w-3 h-3 text-white" />}
              </button>

              {/* Free Keyboard Emoji Input */}
              {item.selected ? (
                <input
                  type="text"
                  maxLength={4} // Allows surrogate pairs and compound emojis
                  value={item.icon}
                  onChange={(e) => setIcon(index, e.target.value)}
                  placeholder="📦"
                  className="w-12 h-10 text-center text-xl rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                  title="Digita un'emoji"
                />
              ) : (
                <div className="w-12 h-10 flex items-center justify-center text-xl text-gray-300 dark:text-gray-600">
                  {item.icon}
                </div>
              )}

              {/* Name + type badge */}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">{item.name}</p>
                <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full mt-0.5 ${typeBadgeColor[item.type]}`}>
                  {typeLabel[item.type]}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-5 flex gap-3 border-t border-gray-100 dark:border-gray-700 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-xl font-medium text-sm hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={totalSelected === 0 || loading}
            className="flex-1 px-4 py-2.5 bg-purple-600 text-white rounded-xl font-semibold text-sm hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Creazione…</>
            ) : (
              <><Wand2 className="w-4 h-4" /> Crea {totalSelected > 0 ? `${totalSelected} categori${totalSelected === 1 ? 'a' : 'e'}` : ''}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RecoverCategoriesDialog;
